// src/hooks/useFarmWishlist.js
// Wishlist management hook for the Farm marketplace.
// Handles listener + toggle with subcollection/top-level fallback.
import { useCallback, useEffect, useState } from 'react';
import { wishlistRepository } from '../repositories/wishlistRepository';
import { useAuth } from './useAuth';
import { errorDev } from '../utils/logger.js';

/**
 * @returns {{
 *   wishlistMap: Record<string, string>,  // productId → wishlistDocId
 *   isWished: (productId: string) => boolean,
 *   toggleWishlist: (product: object) => Promise<boolean>,
 *   wishlistLoading: boolean,
 *   wishlistError: Error | null,
 * }}
 */
export default function useFarmWishlist() {
  const [wishlistMap, setWishlistMap] = useState({});
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistError, setWishlistError] = useState(null);
  const { user } = useAuth();

  // Wishlist snapshot listener
  useEffect(() => {
    if (!user?.uid) {
      setWishlistMap({});
      setWishlistError(null);
      return;
    }

    const uid = user.uid;

    const apply = (rows) => {
      const map = {};
      rows.forEach((item) => {
        const data = item.data ? item.data() : item;
        const docId = item.id || item.wishlistId;
        if (data?.productId) map[data.productId] = docId;
      });
      setWishlistMap(map);
    };

    const handleError = (error) => {
      setWishlistError(error instanceof Error ? error : new Error('Wishlist could not be loaded'));
    };

    return wishlistRepository.subscribeAll(uid, apply, handleError);
  }, [user?.uid]);

  const isWished = useCallback((pid) => Boolean(wishlistMap[pid]), [wishlistMap]);

  const toggleWishlist = useCallback(async (product) => {
    const uid = user?.uid;
    if (!uid) throw new Error('not-authenticated');
    const pid = product.id;
    const existing = wishlistMap[pid];
    setWishlistLoading(true);

    try {
      if (existing) {
        await wishlistRepository.removeWithLegacyFallback(uid, existing);
        return false;
      }

      const sellerId = product.poster?.userId || product.userId || null;
      await wishlistRepository.add(uid, pid, product, sellerId);
      return true;
    } catch (err) {
      errorDev("Operation failed:", err);
      throw err;
    } finally {
      setWishlistLoading(false);
    }
  }, [wishlistMap, user?.uid]);

  return { wishlistMap, isWished, toggleWishlist, wishlistLoading, wishlistError };
}
